"use client";

import * as React from "react";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  Globe,
  MapPin,
  Users,
  Briefcase,
  Bookmark,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { GetContactsButton } from "@/components/features/reports/GetContactsButton";
import SafeImage from "@/components/utils/SafeImage";
import { EntityTitle, EntitySubtitle } from "@/components/ui/design-system";
import {
  EntityActionButton,
  EntityMetaChip,
  EntityBadge,
} from "../_internal/EntityActions";

import type {
  CompanyEntityActions,
  CompanyEntityData,
  CompanyEntityProps,
} from "./CompanyEntity.types";

/* ----------------------------- helpers ---------------------------- */

function formatEmployeeCount(count: number | null | undefined): string | null {
  if (!count) return null;
  if (count >= 10000) return "10,000+";
  if (count >= 5000) return "5,000+";
  if (count >= 1000) return "1,000+";
  if (count >= 500) return "500+";
  return `${count}`;
}

function formatWebsiteDisplay(value?: string): string {
  if (!value) return "";
  return value
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "");
}

function isInteractiveClick(event: React.MouseEvent<HTMLElement>): boolean {
  const target = event.target as HTMLElement | null;
  const interactiveTarget = target?.closest(
    'button, a, input, textarea, select, [role="button"], [role="dialog"], [data-prevent-card-click="true"]',
  );
  return Boolean(
    interactiveTarget && interactiveTarget !== event.currentTarget,
  );
}

/* ------------------------------ Logo ------------------------------ */

const AVATAR_COLORS = [
  '#7f1d1d', // brand burgundy
  '#1b1b1b', // dark neutral
  '#3d2b1f', // mahogany
  '#1e3a3a', // dark teal
  '#2d3748', // slate
  '#3b2d44', // plum
  '#2a3320', // olive
  '#2e2418', // dark walnut
];

function nameToColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

interface CompanyLogoProps {
  company: CompanyEntityData;
  size: number;
  rounded?: boolean;
}

function CompanyLogo({ company, size, rounded = true }: CompanyLogoProps) {
  const [failed, setFailed] = React.useState(false);
  const hasLogo = Boolean(company.logoUrl) && !failed;

  const dimensions = { width: size, height: size } as const;
  const shapeClass = rounded ? "rounded-xl" : "";

  if (hasLogo) {
    return (
      <SafeImage
        src={company.logoUrl as string}
        alt={company.name}
        width={size}
        height={size}
        className={cn(
          "object-cover flex-shrink-0 border border-border",
          shapeClass,
        )}
        onError={() => setFailed(true)}
      />
    );
  }

  const bg = nameToColor(company.name);
  const letter = company.name.trim()[0]?.toUpperCase() ?? '?';
  const fontSize = size >= 48 ? 20 : size >= 40 ? 17 : size >= 28 ? 13 : 11;

  return (
    <div
      className={cn("flex items-center justify-center flex-shrink-0", shapeClass)}
      style={{ ...dimensions, backgroundColor: bg }}
    >
      <span
        style={{ fontSize, lineHeight: 1, color: '#fff', fontWeight: 700, letterSpacing: '0.04em', userSelect: 'none' }}
      >
        {letter}
      </span>
    </div>
  );
}

/* ----------------------------- actions ---------------------------- */

interface ResolveActionsArgs {
  layout: "card" | "row";
  provided?: CompanyEntityActions;
}

function resolveActions({
  layout,
  provided,
}: ResolveActionsArgs): Required<CompanyEntityActions> {
  const defaults: Required<CompanyEntityActions> =
    layout === "row"
      ? { expand: true, research: false, save: false, quickView: false }
      : { quickView: true, research: true, save: false, expand: false };

  return {
    research: provided?.research ?? defaults.research,
    save: provided?.save ?? defaults.save,
    quickView: provided?.quickView ?? defaults.quickView,
    expand: provided?.expand ?? defaults.expand,
  };
}

/* ------------------------------ CARD ------------------------------ */

function CompanyEntityCard({
  company,
  density,
  actions,
  onResearch,
  onSave,
  onQuickView,
  onCardClick,
  footer,
  className,
}: Required<
  Pick<CompanyEntityProps, "company" | "density">
> &
  Pick<
    CompanyEntityProps,
    | "onResearch"
    | "onSave"
    | "onQuickView"
    | "onCardClick"
    | "footer"
    | "className"
  > & { actions: Required<CompanyEntityActions> }) {
  const logoSize = density === "compact" ? 40 : 48;
  const websiteDisplay = formatWebsiteDisplay(
    company.website ?? company.domain,
  );
  const employeeLabel = formatEmployeeCount(company.employeeCount);

  const clickable = Boolean(onCardClick);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onCardClick) return;
    if (event.defaultPrevented) return;
    if (isInteractiveClick(event)) return;
    onCardClick(company);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onCardClick) return;
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onCardClick(company);
    }
  };

  return (
    <div
      className={cn(
        "h-full bg-card border border-border transition-all duration-100 flex flex-col hover:z-10",
        clickable &&
          "cursor-pointer hover:border-[3px] hover:border-[var(--burgundy-core)]",
        "sm:shadow-[var(--shadow-warm-sm)]",
        className,
      )}
      style={{ boxShadow: "var(--shadow-warm-sm)" }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div
        className={cn(
          "flex flex-col gap-3 h-full",
          density === "compact" ? "p-3" : "p-4",
        )}
      >
        <div className="flex items-start gap-3">
          <CompanyLogo company={company} size={logoSize} />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center justify-between gap-3">
              <EntityTitle size="md" truncate>
                {company.name}
              </EntityTitle>
              {company.isExactMatch && <EntityBadge>Exact match</EntityBadge>}
            </div>
            {company.industry && (
              <EntitySubtitle tone="muted" truncate>
                {company.industry}
              </EntitySubtitle>
            )}
          </div>
        </div>

        {company.description && (
          <p className="text-body text-muted-foreground line-clamp-2">
            {company.description}
          </p>
        )}

        <div className="space-y-1.5 pt-2 border-t border-border/40 text-body text-muted-foreground">
          {company.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{company.location}</span>
            </div>
          )}
          {employeeLabel && (
            <div className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{employeeLabel} employees</span>
            </div>
          )}
          {(websiteDisplay || (actions.quickView && onQuickView) || actions.research) && (
            <div className="flex items-center gap-2">
              <div className="flex flex-1 min-w-0 items-center gap-2 text-primary">
                {websiteDisplay && (
                  <>
                    <Globe className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate" title={websiteDisplay}>{websiteDisplay}</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {actions.quickView && onQuickView && (
                  <EntityActionButton
                    variant="secondary"
                    size="sm"
                    icon={Eye}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onQuickView(company);
                    }}
                    className="w-auto shrink-0"
                  >
                    Quick view
                  </EntityActionButton>
                )}
                {actions.research &&
                  (onResearch ? (
                    <EntityActionButton
                      variant="primary"
                      size="sm"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onResearch(company);
                      }}
                      className="w-auto shrink-0"
                    >
                      Get contacts
                    </EntityActionButton>
                  ) : (
                    <GetContactsButton
                      type="company"
                      companyName={company.name}
                      companyId={company.id}
                      companyDescription={company.description || undefined}
                      companyContextData={company.raw}
                      variant="default"
                      size="sm"
                      className="w-auto shrink-0 min-h-8 px-2.5 py-1.5 text-[11px]"
                      style={{ boxShadow: "var(--shadow-warm-sm)" }}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      Get contacts
                    </GetContactsButton>
                  ))}
              </div>
            </div>
          )}
        </div>

        {footer ? (
          <div className="pt-3 border-t border-border/40">{footer}</div>
        ) : null}

        {actions.save && (
          <div className="flex justify-end pt-1">
            <EntityActionButton
              variant="secondary"
              size="sm"
              icon={Bookmark}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onSave?.(company);
              }}
              className="w-auto"
            >
              Save
            </EntityActionButton>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ ROW ------------------------------- */

function CompanyEntityRow({
  company,
  density,
  actions,
  onResearch,
  onQuickView,
  expanded,
  onToggleExpand,
  className,
}: Required<Pick<CompanyEntityProps, "company" | "density">> &
  Pick<
    CompanyEntityProps,
    "onResearch" | "onQuickView" | "expanded" | "onToggleExpand" | "className"
  > & { actions: Required<CompanyEntityActions> }) {
  const handleToggle = React.useCallback(() => {
    onToggleExpand?.(company);
  }, [company, onToggleExpand]);

  const rowClickable = actions.expand && Boolean(onToggleExpand);
  const logoSize = density === "compact" ? 24 : 28;

  return (
    <div
      className={cn(
        "transition-colors",
        density === "compact" ? "px-3 py-2" : "px-3 py-2.5",
        rowClickable && "cursor-pointer hover:bg-muted/30",
        className,
      )}
      style={{ backgroundColor: "var(--background)" }}
      onClick={rowClickable ? handleToggle : undefined}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <CompanyLogo company={company} size={logoSize} rounded={false} />
          <EntityTitle as="span" size="md" truncate>
            {company.name}
          </EntityTitle>
          {company.industry && (
            <span className="text-caption text-muted-foreground truncate">
              {"\u2022 "}
              {company.industry}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {typeof company.jobsCount === "number" && company.jobsCount > 0 && (
            <EntityMetaChip icon={Briefcase} tone="solid">
              {company.jobsCount} {company.jobsCount === 1 ? "job" : "jobs"}
            </EntityMetaChip>
          )}

          {actions.quickView && onQuickView && (
            <EntityActionButton
              variant="secondary"
              size="sm"
              icon={Eye}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onQuickView(company);
              }}
              className="w-auto"
            >
              Quick view
            </EntityActionButton>
          )}

          {actions.research && onResearch && (
            <EntityActionButton
              variant="primary"
              size="sm"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onResearch(company);
              }}
              className="w-auto"
            >
              Get contacts
            </EntityActionButton>
          )}

          {actions.expand && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleToggle();
              }}
              className="flex items-center gap-1.5 px-2 py-1 hover:bg-muted transition-colors border border-border"
              style={{ boxShadow: "var(--shadow-warm-sm)" }}
              aria-label={expanded ? "Collapse contacts" : "Expand contacts"}
            >
              {typeof company.contactsCount === "number" && (
                <span className="text-caption whitespace-nowrap">
                  {company.contactsCount}{" "}
                  {company.contactsCount === 1 ? "contact" : "contacts"}
                </span>
              )}
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Base exported ---------------------------- */

function CompanyEntityBase(props: CompanyEntityProps) {
  const {
    company,
    layout = "card",
    density = "comfortable",
    actions,
    onResearch,
    onSave,
    onQuickView,
    expanded,
    onToggleExpand,
    footer,
    onCardClick,
    className,
  } = props;

  const resolvedActions = resolveActions({ layout, provided: actions });

  if (layout === "row") {
    return (
      <CompanyEntityRow
        company={company}
        density={density}
        actions={resolvedActions}
        onResearch={onResearch}
        onQuickView={onQuickView}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        className={className}
      />
    );
  }

  return (
    <CompanyEntityCard
      company={company}
      density={density}
      actions={resolvedActions}
      onResearch={onResearch}
      onSave={onSave}
      onQuickView={onQuickView}
      onCardClick={onCardClick}
      footer={footer}
      className={className}
    />
  );
}

/* ---------------------------- Public export ---------------------------- */

/**
 * Canonical Company primitive. Prefer `CompanyEntity.Board`,
 * `CompanyEntity.Search`, or `CompanyEntity.Radar` over the base
 * component at call sites.
 */
export const CompanyEntity = Object.assign(CompanyEntityBase, {}) as typeof CompanyEntityBase & {
  Board: React.FC<CompanyEntityProps>;
  Search: React.FC<CompanyEntityProps>;
  Radar: React.FC<CompanyEntityProps>;
  Skeleton: React.FC<{ layout?: "card" | "row" }>;
};
